import type { NavigateFunction } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthActions } from '@/contexts/AuthContext'
import { RegisterError } from '@/services/auth.service'
import type { RegisterFormData } from './useRegisterValidation'

interface UseRegisterSubmissionParams {
    formData: RegisterFormData
    setError: React.Dispatch<React.SetStateAction<string | null>>
    setInviteKeyError: React.Dispatch<React.SetStateAction<string | null>>
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    /** Navigation function (required for dependency injection) */
    navigate: NavigateFunction
}

export const useRegisterSubmission = ({
    formData,
    setError,
    setInviteKeyError,
    setLoading,
    navigate,
}: UseRegisterSubmissionParams) => {
    const { register } = useAuthActions()
    const { t } = useTranslation()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)

        try {
            // Destructure to exclude password fields from registerData
            const {
                password: _password,
                confirmPassword: _confirmPassword,
                ...registerData
            } = formData
            // Explicitly void unused variables to satisfy linter
            void _password
            void _confirmPassword

            await register(
                registerData.username,
                registerData.email,
                formData.password,
                registerData.userLang,
                registerData.targetLang,
                formData.inviteKey || undefined
            )
            void navigate('/dashboard')
        } catch (err: unknown) {
            if (err instanceof RegisterError) {
                const inviteKeyErrorMap: Record<string, string> = {
                    INVITE_KEY_REQUIRED: t('errors:auth.inviteKeyRequired'),
                    INVITE_KEY_INVALID: t('errors:auth.inviteKeyInvalid'),
                    INVITE_KEY_EXPIRED: t('errors:auth.inviteKeyExpired'),
                    INVITE_KEY_REVOKED: t('errors:auth.inviteKeyRevoked'),
                    INVITE_KEY_REDEEMED: t('errors:auth.inviteKeyRedeemed'),
                }
                const inviteKeyMessage = inviteKeyErrorMap[err.code]
                if (inviteKeyMessage) {
                    // Surface invite-key errors both inline on the field (red
                    // outline + helper text + autofocus) and as a top-of-page
                    // alert, so the failure is visible regardless of scroll
                    // position.
                    setInviteKeyError(inviteKeyMessage)
                    setError(inviteKeyMessage)
                } else if (err.code === 'REGISTRATION_CONFLICT') {
                    setError(t('errors:conflict.registration'))
                } else {
                    setError(err.message)
                }
            } else {
                const error = err as {
                    response?: { data?: { detail?: string } }
                    message?: string
                }
                const errorMessage =
                    error.response?.data?.detail ||
                    error.message ||
                    t('errors:auth.accountDeactivated')
                setError(errorMessage)
            }
        } finally {
            setLoading(false)
        }
    }

    return { handleSubmit }
}
